import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { getArtworkBySlug, getArtworks, type Artwork } from "@/lib/api";
import { getFallbackArtworkBySlug } from "@/lib/fallbackArtworks";
import { formatPrice, getArtworkDescription, getArtworkPreviewImage, getArtworkTitle } from "@/lib/utils";
import ArtworkCard from "@/components/ArtworkCard";
import { useCart } from "@/lib/cart";
import { getPrintSizePrices } from "@/lib/printSizes";

export default function ArtworkDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [artwork, setArtwork] = useState<Artwork | null>(null);
  const [relatedArtworks, setRelatedArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [printSize, setPrintSize] = useState("");
  const { addItem, closeCart } = useCart();

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    getArtworkBySlug(slug)
      .then((data) => {
        setArtwork(data);
        setSelectedImage(0);
        if (data.category === "print") setPrintSize(getPrintSizePrices(data)[0]?.size || "");
        return getArtworks({ category: data.category, limit: 4 });
      })
      .then((res) => {
        setRelatedArtworks(res.artworks.filter((item) => item.slug !== slug).slice(0, 3));
      })
      .catch((error) => {
        console.error(error);
        const fallbackArtwork = getFallbackArtworkBySlug(slug);
        if (fallbackArtwork) {
          setArtwork(fallbackArtwork);
          setSelectedImage(0);
          if (fallbackArtwork.category === "print") setPrintSize(getPrintSizePrices(fallbackArtwork)[0]?.size || "");
          setRelatedArtworks([]);
        }
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="page-shell">
        <div className="container">
          <div className="grid gap-8 lg:grid-cols-2">
            <div className="panel aspect-[3/4] animate-pulse bg-[var(--color-sand)]" />
            <div className="panel animate-pulse p-8">
              <div className="mb-4 h-8 w-1/3 rounded-full bg-[var(--color-sand)]" />
              <div className="mb-4 h-16 w-4/5 rounded-[1rem] bg-[var(--color-sand)]" />
              <div className="h-32 rounded-[1rem] bg-[var(--color-sand)]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="page-shell">
        <div className="container">
          <div className="panel p-12 text-center">
            <h1 className="mb-3 text-[2.4rem]">Artwork not found</h1>
            <p className="mb-6" style={{ color: "var(--color-text-muted)" }}>
              {t.common.noResults}
            </p>
            <Link to="/gallery" className="btn-secondary">
              {t.nav.gallery}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const title = getArtworkTitle(artwork, locale);
  const description = getArtworkDescription(artwork, locale);
  const isAvailable = artwork.status === "available";
  const printSizes = artwork.category === "print" ? getPrintSizePrices(artwork) : [];
  const selectedPrintSize = printSize || printSizes[0]?.size || "";
  const selectedPrintPrice = artwork.category === "print"
    ? printSizes.find((size) => size.size === selectedPrintSize)?.price ?? printSizes[0]?.price ?? artwork.price
    : artwork.price;
  const additionalImages = parseAdditionalImages(artwork.additional_images);
  const allImages = [artwork.image_url, ...additionalImages].filter(Boolean) as string[];
  const showPreviousImage = () => setSelectedImage((current) => (current - 1 + allImages.length) % allImages.length);
  const showNextImage = () => setSelectedImage((current) => (current + 1) % allImages.length);

  async function handleBuyNow() {
    if (artwork.category === "original") {
      try {
        const latestArtwork = await getArtworkBySlug(artwork.slug);
        if (latestArtwork.status === "reserved") {
          setArtwork(latestArtwork);
          toast.error(t.artwork.reservationNotice);
          return;
        }
        if (latestArtwork.status !== "available") {
          setArtwork(latestArtwork);
          toast.error(t.artwork.sold);
          return;
        }
      } catch {
        // The checkout validates availability again before it creates a reservation.
      }
    }
    addItem({
      id: artwork.id,
      slug: artwork.slug,
      title,
      category: artwork.category,
      image: getArtworkPreviewImage(artwork),
      price: selectedPrintPrice,
      currency: artwork.currency,
      stockQuantity: artwork.stock_quantity,
      ...(artwork.category === "print" ? { printSize: selectedPrintSize } : {}),
    });
    closeCart();
    navigate("/checkout");
  }

  return (
    <div className="page-shell artwork-detail-page">
      <div className="container">
        <button onClick={() => navigate(-1)} className="btn-ghost mb-4 px-0">
          <ArrowLeft size={16} />
          {t.nav.gallery}
        </button>

        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-4">
            <div
              className="panel relative overflow-hidden"
              onTouchStart={(event) => setTouchStartX(event.touches[0]?.clientX ?? null)}
              onTouchEnd={(event) => {
                if (touchStartX === null) return;
                const distance = event.changedTouches[0].clientX - touchStartX;
                if (Math.abs(distance) > 40 && allImages.length > 1) distance > 0 ? showPreviousImage() : showNextImage();
                setTouchStartX(null);
              }}
            >
              <img
                src={allImages[selectedImage] || "/images/hero-artwork-cropped.jpg"}
                alt={title}
                className="w-full aspect-[3/4] object-cover"
              />
              {allImages.length > 1 && <>
                <button type="button" onClick={showPreviousImage} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-sm" aria-label={locale === "it" ? "Foto precedente" : "Previous photo"}><ChevronLeft size={20} /></button>
                <button type="button" onClick={showNextImage} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-2 shadow-sm" aria-label={locale === "it" ? "Foto successiva" : "Next photo"}><ChevronRight size={20} /></button>
                <span className="absolute bottom-3 right-3 rounded-full bg-black/60 px-3 py-1 text-xs text-white">{selectedImage + 1} / {allImages.length}</span>
              </>}
            </div>
            {allImages.length > 1 && (
              <div className="flex flex-wrap gap-3">
                {allImages.map((image, index) => (
                  <button
                    key={image}
                    onClick={() => setSelectedImage(index)}
                    className="overflow-hidden rounded-2xl border"
                    style={{
                      borderColor:
                        index === selectedImage ? "var(--color-terracotta)" : "var(--color-border)",
                      opacity: index === selectedImage ? 1 : 0.72,
                    }}
                  >
                    <img src={image} alt="" className="h-20 w-20 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <section className="panel p-6 md:p-7">
              <span className="eyebrow">
                {t.categories[artwork.category as keyof typeof t.categories]}
              </span>
              <h1 className="artwork-detail-title">{title}</h1>
              <p
                className="mb-4 text-[1.45rem]"
                style={{ color: isAvailable ? "var(--color-terracotta)" : "var(--color-text-light)" }}
              >
                {isAvailable ? `${t.artwork.available} · ${formatPrice(selectedPrintPrice, artwork.currency)}` : artwork.status === "reserved" ? t.artwork.reserved : t.artwork.sold}
              </p>

              {description && (
                <p className="mb-6 text-[0.93rem] leading-6" style={{ color: "var(--color-text-muted)" }}>
                  {description}
                </p>
              )}

              {artwork.category === "print" && (
                <p className="mb-6 border-l-2 pl-4 text-[0.9rem] leading-6" style={{ borderColor: "var(--color-sand-deep)", color: "var(--color-text-muted)" }}>
                  {t.artwork.printInfo}
                </p>
              )}

              <div className="detail-list artwork-detail-list mb-6">
                {artwork.technique && (
                  <div className="detail-row text-sm">
                    <span style={{ color: "var(--color-text-light)" }}>{t.artwork.technique}</span>
                    <strong>{artwork.technique}</strong>
                  </div>
                )}
                {artwork.dimensions && (
                  <div className="detail-row text-sm">
                    <span style={{ color: "var(--color-text-light)" }}>{t.artwork.dimensions}</span>
                    <strong>{artwork.dimensions}</strong>
                  </div>
                )}
                {artwork.year && (
                  <div className="detail-row text-sm">
                    <span style={{ color: "var(--color-text-light)" }}>{t.artwork.year}</span>
                    <strong>{artwork.year}</strong>
                  </div>
                )}
                <div className="detail-row text-sm">
                  <span style={{ color: "var(--color-text-light)" }}>{t.artwork.category}</span>
                  <strong>{t.categories[artwork.category as keyof typeof t.categories]}</strong>
                </div>
              </div>

              {isAvailable ? (
                <>
                {artwork.category === "print" && <label className="mb-3 block text-sm" style={{ color: "var(--color-text-muted)" }}>
                  {locale === "it" ? "Dimensione della stampa" : "Print size"}
                  <select className="admin-input mt-1" value={selectedPrintSize} onChange={(event) => setPrintSize(event.target.value)}>
                    {printSizes.map((size) => (
                      <option key={size.size} value={size.size}>{size.size} · {formatPrice(size.price, artwork.currency)}</option>
                    ))}
                  </select>
                </label>}
                <button onClick={() => void handleBuyNow()} className="btn-primary w-full">
                  {t.artwork.buyNow}
                  <ArrowRight size={16} />
                </button>
                </>
              ) : artwork.status === "reserved" ? (
                <p className="rounded-xl border px-4 py-3 text-sm leading-6" style={{ borderColor: "var(--color-sand-deep)", color: "var(--color-text-muted)" }}>
                  {t.artwork.reservationNotice}
                </p>
              ) : (
                <Link to="/contact" className="btn-secondary w-full">
                  {t.artwork.inquire}
                </Link>
              )}
            </section>

            <section className="panel p-5">
              <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>{artwork.category === "print" ? t.artwork.printShippingInfo : t.artwork.shippingInfo}</p>
              <p className="mt-2 text-sm" style={{ color: "var(--color-text-muted)" }}>{t.artwork.trackingInfo}</p>
            </section>
          </div>
        </div>

        {relatedArtworks.length > 0 && (
          <section className="section-padding pb-0">
            <div className="section-heading">
              <div>
                <span className="eyebrow">{t.artwork.relatedWorks}</span>
                <h2>{t.artwork.relatedWorks}</h2>
              </div>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {relatedArtworks.map((item) => (
                <ArtworkCard key={item.id} artwork={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function parseAdditionalImages(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string" && item.length > 0) : [];
  } catch {
    return [];
  }
}
